import { FISH_KINDS } from '@if/sim-fish';
import { type Комплект, КОМПЛЕКТ_ГНЁЗДА, xpForLevel } from '@if/sim-items';

/** Все комплекты игры — по нему и сверяется присланный. */
const KIT_ORDER = Object.keys(КОМПЛЕКТ_ГНЁЗДА) as Комплект[];
import { t } from '../i18n.js';
import { gameServerUrl } from '../net-config.js';
import { session } from '../session.js';
import { сессияМертва } from './session-guard.js';

/**
 * Всё, чем живёт база: водоёмы, кошелёк, садок, снасти, рекорды.
 *
 * ⚠️ ЭТОТ ФАЙЛ НЕ ЗНАЕТ ПРО ДВИЖОК, И ЭТО УСЛОВИЕ, А НЕ СОВПАДЕНИЕ. База
 * открывается ДО того, как скачан Babylon: человек выбирает водоём, глядя на
 * журнал, и только нажатие «На рыбалку» тянет восемь мегабайт движка. Стоит
 * сюда попасть хоть одному ввозу из игры — и вся эта выгода пропадёт разом.
 *
 * ⚠️ НИ ОДИН ОТКАЗ НЕ ГАСИТ ЭКРАН. Ручки берутся через allSettled: не ответил
 * садок — журнал всё равно откроется, просто в той строке будет прочерк.
 * Иначе одна упавшая таблица оставляла бы игрока перед чёрным экраном без
 * единого слова о том, что случилось.
 */

/** Водоём, обведённый админом по настоящей карте. */
export interface Водоём {
  /** Имя файла карты — по нему локация и грузится. */
  file: string;
  /**
   * Ключевое название: имя комнаты, поле уловов, связь с базой.
   *
   * ⚠️ НА ЭКРАН ЕГО НЕ СТАВЯТ — для показа есть `names`. Здесь оно потому,
   * что по нему заводится комната мультиплеера и пишутся уловы: сделай его
   * разным на четырёх языках, и игроки с русским и английским оказались бы
   * в разных комнатах одного озера.
   */
  name: string;
  /** Названия для показа по языкам. Пусто — водоём размечен до этого поля. */
  names: Record<string, string>;
  /** Когда сохранён, мс. */
  when: number;
  kb: number;
  /**
   * Что игрок должен знать о водоёме до выезда.
   *
   * ⚠️ ЭТО ЗАМЕНА ДАТЕ СОХРАНЕНИЯ. В списке стояло «размечен 8 августа» —
   * сведение о работе админа, а не о рыбалке. Игрок же перед выездом
   * спрашивает себя о другом: велик ли водоём, глубок ли, кто в нём водится.
   *
   * Числа берутся из самого файла карты (окно выпечки, наибольшая глубина) и
   * из маски берега (зеркало); старый файл может их не иметь — тогда null, и
   * строка просто короче.
   */
  windowM: number | null;
  depthM: number | null;
  mirrorKm2: number | null;
  /**
   * Страна — по ней в лобби раскладываются закладки.
   *
   * ⚠️ ВЫДЕЛЯЕТСЯ ИЗ СТРОКИ МЕСТА, КОТОРУЮ ПИШЕТ OSM ПРИ ОБВОДКЕ, а не
   * задаётся руками. Поэтому пустой она бывает: у водоёма, размеченного без
   * связи с геослужбой, места нет вовсе. Такие собираются под отдельной
   * закладкой — прятать их нельзя, они настоящие.
   */
  country: string;
  /** true — country это код по ISO, и название берётся из словаря. */
  countryIsCode: boolean;
  рыба: string[];
  /** Требуемый уровень для доступа к локации */
  minLevel: number;
  /** Широта центра. */
  lat: number | null;
  /** Долгота центра. */
  lon: number | null;
}

export interface Кошелёк {
  coins: number;
  gold?: number;
  xp: number;
  level: number;
}

export interface РыбаВСадке {
  id: number;
  kind: string;
  kg: number;
  lengthCm: number;
  location: string;
  at: number;
  coins: number;
}

export interface Снасти {
  items: string[];
  equipped: Record<string, string>;
  /**
   * Чем ловят сейчас: спиннингом или удочкой.
   *
   * ⚠️ ПРИХОДИТ С СЕРВЕРА, А НЕ УГАДЫВАЕТСЯ ПО НАДЕТОМУ. Угадать можно было
   * бы по классу удилища, но у игрока без удилища вовсе (продал, ещё не
   * купил) угадывать было бы не по чему — а гнёзда показывать надо.
   */
  kit?: Комплект;
}

/** Рекорд по виду. Полей у сервера может прибавиться — читаем мягко. */
export interface Рекорд {
  kind?: string;
  kg?: number;
  grams?: number;
  /**
   * Имя рыболова.
   *
   * ⚠️ ЗДЕСЬ БЫЛО `email`, И ЭТО БЫЛА УТЕЧКА. Ручка /catches/top открыта без
   * токена вовсе: дёрни её — и получишь почты всех, кто хоть раз ловил.
   * Клиент резал их до собачки при показе, но резал уже у себя, а по сети
   * они приезжали целиком. Теперь сервер почту не отдаёт.
   */
  name?: string;
  location?: string;
  /**
   * Когда поймана, в миллисекундах эпохи.
   *
   * ⚠️ СЕРВЕР СЛАЛ ЭТО ПОЛЕ И ДО ТОГО, КАК ОНО ЗДЕСЬ ПОЯВИЛОСЬ. `TopRow.at`
   * заполняется из `caught_at` в обеих ручках — и в рекордах, и в истории, — а
   * ответ мы не разбираем, а приводим к типу: число доезжало и лежало в
   * объекте, но дотянуться до него было нельзя, о нём просто не было записано.
   * Не «времени нет в базе», а «никто его не объявил».
   */
  at?: number;
}

export interface ДанныеБазы {
  водоёмы: Водоём[];
  /**
   * Список не пришёл: сервер не отозвался или отказал.
   *
   * ⚠️ ЭТО НЕ ТО ЖЕ, ЧТО ПУСТОЙ СПИСОК. Пустой — значит админ ещё ничего не
   * завёл; сбой — значит завёл, но спросить не вышло. Показывать их одним
   * текстом «водоёмов пока нет» нельзя: человек пойдёт заводить водоёмы там,
   * где надо чинить связь.
   */
  списокНеОтвечает: boolean;
  кошелёк: Кошелёк | null;
  садок: РыбаВСадке[];
  снасти: Снасти | null;
  рекорды: Рекорд[];
  мои: Рекорд[];
}

/**
 * Запрос к игровому серверу с ключом в теле.
 *
 * ⚠️ КЛЮЧ ИДЁТ В ТЕЛЕ, А НЕ В АДРЕСЕ. Ручки сервера все до одной POST и берут
 * хозяина из токена; почту они не принимают намеренно — иначе кошелёк соседа
 * открывался бы одной подставленной строкой.
 */
async function спросить<T>(путь: string, поле: string, запас: T): Promise<T> {
  try {
    const res = await fetch(gameServerUrl(путь), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: session.token }),
    });
    // ⚠️ ПРОВЕРЯЕМ ДО ЗАПАСА. Ниже отказ любого рода превращается в пустое
    // значение, и мёртвая сессия выглядела бы просто пустым журналом: ни
    // монет, ни садка, ни рекордов — а причина не названа.
    if (сессияМертва(res)) return запас;
    if (!res.ok) return запас;
    const data = (await res.json()) as Record<string, unknown>;
    const v = data[поле];
    return v === undefined || v === null ? запас : (v as T);
  } catch {
    return запас;
  }
}

/**
 * Список водоёмов.
 *
 * ⚠️ СПРАШИВАЕМ БАЗУ, А НЕ ФАЙЛЫ, И ЭТО ПЕРЕЛОМ, А НЕ МЕЛОЧЬ.
 *
 * Прежде список брался у сервера разработки — ручка `/__world-list` читала
 * `public/worlds/*.json` с диска. Она помечена `apply: 'serve'`, то есть в
 * СОБРАННОЙ ИГРЕ её нет вообще: игрок получал пустой список и «водоёмов пока
 * нет» — при полностью исправной игре и тринадцати озёрах в базе. Отдать игру
 * кому-нибудь, кроме себя, было нельзя.
 *
 * Теперь источник один — таблица `locations` игрового сервера. Та же, по
 * которой сервер считает глубину и разыгрывает поклёвки. Это важнее удобства:
 * пока источников было два, водоём мог быть в списке и не быть у сервера — и
 * тогда в нём молча не клевало НИЧЕГО, а понять это было нечем.
 *
 * ⚠️ КАРТЫ ПО СЕТИ НЕ ЕДУТ. Ручка отдаёт выжимку — около двухсот байт на
 * водоём вместо всей карты. Файловая тянула 400 КБ на каждое открытие журнала,
 * и почти всё это был `outline.landDm`, рельеф суши, к списку не относящийся.
 *
 * ⚠️ РЫБА ЗАПРАШИВАЕТСЯ ОДНИМ ЗАПРОСОМ НА ВСЕ ВОДОЁМЫ. По запросу на строку
 * список открывался бы тем медленнее, чем больше локаций завёл админ.
 */
async function водоёмы(): Promise<{ список: Водоём[]; сбой: boolean }> {
  /*
   * ⚠️ ЭТОТ ЗАПРОС ИДЁТ МИМО `спросить`, И НАРОЧНО. Тот превращает ЛЮБОЙ отказ
   * — обрыв, 500, запрет по CORS — в пустое значение. Для кошелька это верно:
   * пустой кошелёк не соврёт. Но пустой список водоёмов НЕОТЛИЧИМ от честного
   * «админ ещё ничего не завёл», и игрок читал бы «водоёмов пока нет», когда на
   * деле не отозвался сервер. Ровно этот сорт молчания уже стоил вечера, когда
   * сервер не находил карту и не говорил ни слова.
   */
  let сбой = false;
  const список = await fetch(gameServerUrl('/locations/list'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: session.token }),
  })
    .then(async (r) => {
      if (!r.ok) {
        сбой = true;
        return [] as Партия[];
      }
      const d = (await r.json()) as { locations?: unknown };
      return Array.isArray(d.locations) ? (d.locations as Партия[]) : [];
    })
    .catch(() => {
      сбой = true;
      return [] as Партия[];
    });

  const рыба = await спросить<Record<string, string[]>>('/locations/fish', 'byLocation', {});

  return {
    сбой,
    список: список.map((в) => ({
      file: в.file,
      name: в.name,
      names: в.names ?? {},
      when: в.updatedAt ?? в.when ?? 0,
      kb: в.kb,
      windowM: в.windowM ?? null,
      depthM: в.depthM ?? null,
      mirrorKm2: в.mirrorKm2 ?? null,
      country: в.country ?? '',
      countryIsCode: в.countryIsCode === true,
      // Нет записи — водится всё. Так задумано, чтобы водоёмы, размеченные до
      // появления галочек, не обезрыбели разом.
      рыба: рыба[в.name] ?? ВСЯ_РЫБА,
      minLevel: в.minLevel ?? 1,
      lat: в.lat ?? null,
      lon: в.lon ?? null,
    })),
  };
}

/**
 * Сырая строка из ручки списка — до соединения с рыбой.
 *
 * ⚠️ ВРЕМЯ ПРИХОДИТ ПОД ИМЕНЕМ `updatedAt`, А НЕ `when`. В базе это время
 * правки строки, у файловой ручки было время правки файла — величина та же по
 * смыслу, имя другое. Оба поля объявлены необязательными, чтобы переход не
 * зависел от того, какая сторона обновилась первой.
 */
interface Партия {
  file: string;
  name: string;
  names?: Record<string, string>;
  when?: number;
  updatedAt?: number;
  kb: number;
  windowM?: number | null;
  depthM?: number | null;
  mirrorKm2?: number | null;
  country?: string;
  countryIsCode?: boolean;
  minLevel?: number;
  lat?: number | string | null;
  lon?: number | string | null;
}

/**
 * Все виды рыбы — то, что стоит у водоёма по умолчанию.
 *
 * ⚠️ БЕРЁТСЯ ИЗ ПАКЕТА, А НЕ ПЕРЕЧИСЛЯЕТСЯ ЗДЕСЬ. Свой список неизбежно
 * разошёлся бы с игрой: добавили леща — и он либо не появится в галочках,
 * либо появится, но ловиться не будет.
 */
export const ВСЯ_РЫБА: string[] = Object.keys(FISH_KINDS);

/** Сохранить выбор админа. Бросит словами, если сервер откажет. */
export async function задатьРыбу(водоём: string, виды: string[]): Promise<string[]> {
  const d = await послать('/locations/fish/set', { location: водоём, kinds: виды });
  return Array.isArray(d.kinds) ? (d.kinds as string[]) : виды;
}

/**
 * Снасти: что в рюкзаке и что надето.
 *
 * ⚠️ ЭТА РУЧКА ОТДАЁТ ДВА ПОЛЯ СРАЗУ, А НЕ ОДНО. Ответ у неё — `{items,
 * equipped, wallet}`, без обёртки. Возьми отсюда только `items` — и журнал
 * покажет, сколько всего снастей, но не скажет, какой удочкой ловят: поле
 * `equipped` тихо станет пустым, и разница будет заметна лишь глазами.
 */
async function снасти(): Promise<Снасти | null> {
  try {
    const res = await fetch(gameServerUrl('/inventory'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: session.token }),
    });
    if (сессияМертва(res)) return null;
    if (!res.ok) return null;
    const d = (await res.json()) as {
      items?: string[];
      equipped?: Record<string, string>;
      kit?: string;
    };
    /*
     * ⚠️ КОМПЛЕКТ ЧИТАЕМ ЗДЕСЬ ТОЖЕ, И БЕЗ ЭТОГО ГНЁЗДА ВРУТ. Инвентарь
     * показывает гнёзда по комплекту; потеряй его тут — удочный набор рисовался
     * бы спиннинговым, и в нём стояла бы пустая «Катушка», которой у удочки нет
     * вовсе. Ровно это и вышло: остальные две ручки комплект возвращали, а эта,
     * главная, — нет.
     */
    /*
     * ⚠️ СВЕРЯЕМ СО СПИСКОМ, А НЕ С ОДНИМ СЛОВОМ. Здесь стояло «удочка или
     * спиннинг», и комплектов ровно столько и было. Появились болонский и
     * фидерный — и такая проверка молча свела бы их к спиннингу: игрок увидел
     * бы гнездо катушки там, где нужен поплавок, и наоборот.
     */
    const пришло = String(d.kit ?? 'спиннинг') as Комплект;
    const kit: Комплект = KIT_ORDER.includes(пришло) ? пришло : 'спиннинг';
    return { items: d.items ?? [], equipped: d.equipped ?? {}, kit };
  } catch {
    return null;
  }
}

/**
 * Собрать всё разом.
 *
 * ⚠️ ЗАПРОСЫ ИДУТ ПАРАЛЛЕЛЬНО. Их шесть; выстроенные в цепочку, они дали бы
 * полсекунды на ровном месте — и это при том, что база должна открываться
 * мгновенно, ей ведь ничего не надо строить.
 */
export async function собратьБазу(): Promise<ДанныеБазы> {
  const [w, к, с, сн, р, м] = await Promise.all([
    водоёмы(),
    спросить<Кошелёк | null>('/wallet', 'wallet', null),
    спросить<РыбаВСадке[]>('/keepnet', 'fish', []),
    снасти(),
    спросить<Рекорд[]>('/catches/top', 'top', []),
    спросить<Рекорд[]>('/catches/mine', 'catches', []),
  ]);
  return {
    водоёмы: w.список,
    списокНеОтвечает: w.сбой,
    кошелёк: к,
    садок: с,
    снасти: сн,
    рекорды: р,
    мои: м,
  };
}

/**
 * Сменить имя. Вернёт то, что записалось (сервер обрезает лишние пробелы),
 * либо бросит с человеческим текстом отказа.
 *
 * ⚠️ ПОЧТУ НЕ ШЛЁМ ДАЖЕ ЗДЕСЬ. Владельца сервер берёт из токена; прими он её
 * доводом — и любой менял бы соседу имя и пароль одной подставленной строкой.
 */
export async function сменитьИмя(имя: string): Promise<string> {
  const d = await послать('/account/name', { name: имя });
  return typeof d.name === 'string' ? d.name : имя;
}

/** Сменить пароль. Старый не спрашивается — так решил владелец игры. */
export async function сменитьПароль(пароль: string): Promise<void> {
  await послать('/account/password', { password: пароль });
}

/**
 * Продать рыбу из садка. Возвращает новый кошелёк.
 *
 * ⚠️ КАССА ЖИВЁТ НА БАЗЕ, И ЭТО ПРАВИЛО ИГРЫ, А НЕ УДОБСТВО. В водоёме садок
 * только показывают: набил сотню — возвращайся. Продавай мы и с воды, предел
 * садка не значил бы ничего, потому что опустошить его можно было бы не
 * вставая с места.
 *
 * ⚠️ И ХОДИМ МЫ НЕ ЧЕРЕЗ inventory.ts, ХОТЯ ТАМ ЕСТЬ ТЕ ЖЕ РУЧКИ. Инвентарь
 * получает токен от игры (initInventory), а игра до базы ещё не загружена:
 * его запрос ушёл бы с пустым ключом, сервер ответил бы 401, и сторож сессии
 * выбросил бы человека на форму входа прямо из магазина.
 */
export async function продать(ids: number[]): Promise<{ продано: number; кошелёк: Кошелёк }> {
  const d = await послать('/shop/sell', { ids });
  return {
    продано: Number(d.sold ?? 0),
    кошелёк: (d.wallet ?? { coins: 0, xp: 0, level: 1 }) as Кошелёк,
  };
}

/**
 * Надеть предмет на базе.
 *
 * ⚠️ СНАРЯЖАЮТСЯ ПЕРЕД ВЫЕЗДОМ, А НЕ НА ВОДЕ, И ДО СИХ ПОР ЭТОГО НЕЛЬЗЯ БЫЛО.
 * Экран снаряжения жил только в меню игры: чтобы сменить удочку, надо было
 * сперва доехать до водоёма. Выходило наоборот здравому смыслу.
 *
 * ⚠️ ХОЗЯИН НАДЕТОГО — СЕРВЕР, И ПОРЯДОК ТОТ ЖЕ, ЧТО В ИГРЕ: сперва запись,
 * потом показ. Примени мы сперва у себя, отказ («этого предмета у вас нет»)
 * оставил бы на экране снасть, которой у человека не куплено.
 *
 * ⚠️ И СНОВА НЕ ЧЕРЕЗ inventory.ts. Тот берёт ключ у игры, а игра до базы не
 * загружена: запрос ушёл бы с пустым ключом, сервер ответил бы 401, и сторож
 * сессии выбросил бы человека на форму входа прямо из инвентаря.
 */
export async function надеть(гнездо: string, defId: string): Promise<Снасти> {
  const d = await послать('/inventory/equip', { slot: гнездо, defId });
  return {
    items: (d.items ?? []) as string[],
    equipped: (d.equipped ?? {}) as Record<string, string>,
    kit: (d.kit ?? 'спиннинг') as Снасти['kit'],
  };
}

/** Купить снасть. Возвращает обновлённые списки и кошелёк. */
export async function купить(defId: string): Promise<{ снасти: Снасти; кошелёк: Кошелёк }> {
  const d = await послать('/shop/buy', { defId });
  return {
    снасти: {
      items: (d.items ?? []) as string[],
      equipped: (d.equipped ?? {}) as Record<string, string>,
      kit: (d.kit ?? 'спиннинг') as Снасти['kit'],
    },
    кошелёк: (d.wallet ?? { coins: 0, xp: 0, level: 1 }) as Кошелёк,
  };
}

/**
 * Отправить и разобрать ответ.
 *
 * ⚠️ ОТКАЗ ДОЛЖЕН ДОЙТИ СЛОВАМИ, А НЕ КОДОМ. Сервер объясняет по-русски, что
 * не так: «это имя уже занято», «пароль короче шести знаков». Проглоти мы
 * тело ответа и покажи «ошибка 400» — человек остался бы гадать, что чинить.
 */
async function послать(
  путь: string,
  тело: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const res = await fetch(gameServerUrl(путь), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: session.token, ...тело }),
  });
  // ⚠️ ДО РАЗБОРА ТЕЛА: у отказа тела может не быть вовсе, а страница уже
  // уходит на перезагрузку — разбирать нечего и незачем.
  if (сессияМертва(res)) throw new Error(отказСловами({}));
  const d = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) throw new Error(отказСловами(d));
  return d;
}

/**
 * Отказ сервера на языке игрока.
 *
 * ⚠️ ПЕРЕВОДИМ КЛЮЧ, А ТЕКСТ БЕРЁМ ЗАПАСНЫМ. Сервер не знает языка игрока —
 * язык живёт в браузере — и потому шлёт и то и другое: `key` для перевода,
 * `error` на случай, если ключа в словаре не нашлось. Без этого «Не хватает
 * монет» появлялось бы посреди английского интерфейса.
 */
export function отказСловами(d: Record<string, unknown>): string {
  // ⚠️ И САМ ЗАПАСНОЙ ТЕКСТ ТОЖЕ ПЕРЕВОДИТСЯ. Здесь стояло русское «Сервер не
  // ответил» строкой, при том что ключ err.no_answer лежал в четырёх словарях
  // и не звался ниоткуда. Случай этот — не редкий угол: так отвечает КАЖДЫЙ
  // упавший или недоступный сервер, то есть первое, что видит игрок, когда
  // что-то не работает, — и видел он это по-русски на любом языке.
  const текст = typeof d.error === 'string' ? d.error : t('err.no_answer');
  if (typeof d.key !== 'string') return текст;
  const vars = (d.vars ?? {}) as Record<string, string | number>;
  const слово = t(d.key, vars);
  return слово === d.key ? текст : слово;
}

/** Сколько всего весит садок, кг. */
export function весСадка(садок: РыбаВСадке[]): number {
  let s = 0;
  for (const р of садок) s += р.kg;
  return s;
}

/**
 * Сколько опыта до следующего уровня, 0..1.
 *
 * ⚠️ ПОРОГ БЕРЁТСЯ ИЗ ПАКЕТА, А НЕ ПЕРЕПИСЫВАЕТСЯ ЗДЕСЬ. По этой самой
 * формуле сервер считает уровень; повтори её на глаз — и полоска разойдётся
 * с уровнем, причём молча. Первый набросок так и вышел: у меня стояло
 * 100·(n−1)·n вместо 80·(n−1)^1.75, и на седьмом уровне полоска показывала бы
 * треть там, где опыта набрано почти на весь.
 *
 * Пакет чистый — данные о рыбе да числа, ни строчки движка, — потому его и
 * можно звать с экрана, живущего до Babylon.
 */
export function доляУровня(кошелёк: Кошелёк): number {
  const уровень = Math.max(1, кошелёк.level);
  const низ = xpForLevel(уровень);
  const верх = xpForLevel(уровень + 1);
  if (верх <= низ) return 1;
  return Math.max(0, Math.min(1, (кошелёк.xp - низ) / (верх - низ)));
}

/** Сколько опыта не хватает до следующего уровня. */
export function осталосьОпыта(кошелёк: Кошелёк): number {
  return Math.max(0, xpForLevel(Math.max(1, кошелёк.level) + 1) - кошелёк.xp);
}

// ── Администрирование игроков ─────────────────────────────────────────────
//
// ⚠️ ЭТИ ТРИ РУЧКИ ОТКРЫТЫ ТОЛЬКО АДМИНУ, И РЕШАЕТ ЭТО СЕРВЕР ПО ТОКЕНУ.
// Здесь нет ни одной проверки роли намеренно: спрячь мы кнопку в интерфейсе и
// понадеемся на это — доступ держался бы на том, что игрок не открывал консоль.

export interface ИгрокСтрока {
  email: string;
  name: string;
  role: string;
  level: number;
  coins: number;
  catches: number;
  createdAt: number;
  lastSeen: number | null;
  lastIp: string;
  banned: boolean;
  banReason: string;
  /** Гость: учётка без пароля, заведённая одной кнопкой. */
  guest: boolean;
}

export interface ИгрокКарточка extends ИгрокСтрока {
  xp: number;
  вещей: number;
  уловы: { kind: string; grams: number; lengthCm: number; location: string; at: number }[];
  входы: { ip: string; ok: boolean; at: number }[];
}

/** Страница списка игроков: строки и сколько их всего. */
export async function игроки(
  q: string,
  limit: number,
  offset: number,
): Promise<{ список: ИгрокСтрока[]; всего: number }> {
  const d = await послать('/admin/players', { q, limit, offset });
  return {
    список: Array.isArray(d.players) ? (d.players as ИгрокСтрока[]) : [],
    всего: Number(d.total ?? 0),
  };
}

/** Карточка одного игрока. */
export async function игрок(email: string): Promise<ИгрокКарточка> {
  const d = await послать('/admin/player', { email });
  return d.player as ИгрокКарточка;
}

/** Действие над игроком: ban, unban, role, name, password, coins. */
export async function надИгроком(email: string, what: string, value: unknown): Promise<void> {
  await послать('/admin/player/set', { email, what, value });
}

/**
 * Превратить гостя в постоянный аккаунт.
 *
 * ⚠️ НОВЫЙ ТОКЕН НАДО СОХРАНИТЬ ТУТ ЖЕ. Он выводится из почты, а она сменилась:
 * прежний ключ перестаёт годиться в тот же миг. Не запиши мы новый — игрок был
 * бы выброшен из игры собственным успехом, при первом же запросе к серверу.
 */
export async function превратитьВПостоянный(
  email: string,
  пароль: string,
): Promise<{ email: string; name: string }> {
  const d = await послать('/account/upgrade', { email, password: пароль });
  const токен = String(d.token ?? '');
  const кто = (d.user ?? {}) as { email?: string; name?: string };
  if (токен !== '') {
    session.token = токен;
    localStorage.setItem('if-auth-token', токен);
    localStorage.setItem('if-auth-email', String(кто.email ?? email));
  }
  return { email: String(кто.email ?? email), name: String(кто.name ?? '') };
}
